import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextareaAutosize  from '@mui/material/TextareaAutosize';
import Divider from '@mui/material/Divider';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { standardDeviation, average, andersonDarlingTest, dAgostinoPearsonTest, kolmogorovSmirnov } from '../lib/stats'
import toFixed from '../lib/toFixed'

function NormalityTest() {
  const [data, setData] = useAutoSaveState('data', '')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [result, setResult] = useState({})
  const [histogramData, setHistogramData] = useState({})
  const { ks, ad, da } = result

  const calculate = () => {
    const input = data.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    console.log(input)
    const output = {
      ks: kolmogorovSmirnov(input),
      ad: andersonDarlingTest(input),
      da: dAgostinoPearsonTest(input),
      sd: standardDeviation(input),
      avg: average(input)
    }
    console.log({ output })
    const bins = createBins(input, 15)
    console.log({ bins })
    setHistogramData(bins)
    setResult(output)
    setPreview(`p(KS): ${toFixed(output.ks.p, 3)}, p(AD): ${toFixed(output.ad.p, 3)}, p(DA): ${toFixed(output.da.p, 3)}`)
    window.gtag('event', 'calculate-normality')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Normality Tests
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data}
                  onChange={e => setData(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result.ks && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Normality Test Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px' }} />

            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Average:</b> {toFixed(result.avg, 3)}</Typography>
              <Typography variant="body1"><b>Standard Deviation:</b> {toFixed(result.sd, 3)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px' }} />

            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Anderson-Darling Test Results</b></Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1" sx={{ color: ad.p < 0.05 ? 'red' : 'green', textAlign: 'center', fontWeight: 'bold' }} >
                {ad.p < 0.05 ? 'Not normally distributed (p < 0.05)' : 'Normally distributed (p >= 0.05)'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>P-Value:</b> {toFixed(ad.p, 3)}</Typography>
              <Typography variant="body1"><b>AD Stastic:</b> {toFixed(ad.ADStatistic, 3)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />

            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>D'Agostino-Pearson Test Results</b></Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1" sx={{ color: da.p < 0.05 ? 'red' : 'green', textAlign: 'center', fontWeight: 'bold' }} >
                {da.p < 0.05 ? 'Not normally distributed (p < 0.05)' : 'Normally distributed (p >= 0.05)'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>P-Value:</b> {toFixed(da.p, 3)}</Typography>
              <Typography variant="body1"><b>Skewness:</b> {toFixed(da.skewness, 3)}</Typography>
              <Typography variant="body1"><b>Kurtosis:</b> {toFixed(da.kurtosis, 3)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />

            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Kolmogorov-Smirnov Test Results</b></Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1" sx={{ color: ks.p < 0.05 ? 'red' : 'green', textAlign: 'center', fontWeight: 'bold' }} >
                {ks.p < 0.05 ? 'Not normally distributed (p < 0.05)' : 'Normally distributed (p >= 0.05)'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>P-Value:</b> {toFixed(ks.p, 3)}</Typography>
              <Typography variant="body1"><b>KS Stastic:</b> {toFixed(ks.ksStatistic, 3)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />

          </Paper>
        </Grid>}
        {result.ks && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <KSHistogram data={histogramData} />
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

const KSHistogram = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="bin" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="count" fill="#8884d8" />
      </BarChart>
    </ResponsiveContainer>
  );
};

function createBins (data, numberOfBins) {
  const minData = Math.min(...data);
  const maxData = Math.max(...data);
  const range = maxData - minData;
  const binSize = range / numberOfBins;
  const bins = Array(numberOfBins).fill(0);

  data.forEach(stat => {
    const binIndex = Math.floor((stat - minData) / binSize);
    bins[binIndex < numberOfBins ? binIndex : numberOfBins - 1]++;
  });

  return bins.map((count, index) => ({
    bin: `${(minData + index * binSize).toFixed(2)}-${(minData + (index + 1) * binSize).toFixed(2)}`,
    count
  }));
}

export default function WrappedNormalityTest() {
  return (
    <AutoSaveProvider>
      <NormalityTest />
    </AutoSaveProvider>
  );
}
